import { createSelector } from 'reselect';

// Types
import { RootState } from 'types/RootState.types';

// Config
import {
  getDeliveryOptionsSelector,
  getShowAmendQuantityNotificationSelector,
  getShowClickCollectNowAvailableMessageSelector,
  getShowCollectionHelpSelector,
  getBatchesDeliveryMethodsApiCallActiveSelector,
  getDeliveryBatchesProcessingSelector,
  getDeliveryProceedingToPaymentSelector,
  getPutDeliveryAddressApiCallActiveSelector,
} from '@redux/reducers/delivery/deliverySelector';
import {
  getProceedingToPaymentSelector,
  getSavedCollectionPointsApiCallLoadingSelector,
  getCollectionPointDatesApiCallActiveSelector,
} from '@redux/reducers/click-and-collect/clickAndCollectSelector';
import {
  getIsExpressPayment,
  getInitPaymentPageCallActiveSelector,
  getPaymentProcessingSelector,
} from '@redux/reducers/payment/paymentSelector';
import {
  getIsPartnerDiscountProcessingSelector,
} from '@redux/reducers/user/userSelector';
import {
  getSubmitOrderFailedSelector,
  getOrderFormItemsSelector,
} from '@redux/reducers/order-form/orderFormSelector';
import { getConfigFeaturesSelector } from '@redux/reducers/config/configSelector';
import getDeliveryTitleText from '@utils/delivery/getDeliveryTitleText';
import { shouldShowThresholdBar } from '@utils/orderform/shouldShowThresholdBar';

import featureConstants from '@constants/featureConstants';

const getTitleText = (state: RootState) => getDeliveryTitleText(state);

export const getTabsDisabled = createSelector([
  getIsPartnerDiscountProcessingSelector,
  getPutDeliveryAddressApiCallActiveSelector,
  getBatchesDeliveryMethodsApiCallActiveSelector,
  getDeliveryBatchesProcessingSelector,
  getProceedingToPaymentSelector,
  getInitPaymentPageCallActiveSelector,
  getDeliveryProceedingToPaymentSelector,
  getPaymentProcessingSelector,
  getSavedCollectionPointsApiCallLoadingSelector,
  getCollectionPointDatesApiCallActiveSelector,
], (
  isPartnerDiscountProcessing,
  putDeliveryAddressApiCallActive,
  batchesDeliveryMethodsApiCallActive,
  deliveryBatchesProcessing,
  proceedingToPayment,
  initPaymentPageCallActive,
  deliveryProceedingToPayment,
  paymentProcessing,
  getSavedCollectionPointsApiCallLoading,
  getCollectionPointDatesApiCallActive,
) => {
  return isPartnerDiscountProcessing
    || putDeliveryAddressApiCallActive
    || batchesDeliveryMethodsApiCallActive
    || deliveryBatchesProcessing
    || proceedingToPayment
    || initPaymentPageCallActive
    || deliveryProceedingToPayment
    || paymentProcessing
    || getSavedCollectionPointsApiCallLoading
    || getCollectionPointDatesApiCallActive;
});

const getDeliveryCollectionRadioButtonsFeature = ((state: RootState) => {
  const features = getConfigFeaturesSelector(state);
  return features?.find(feature => feature?.id === featureConstants.DELIVERY_COLLECTION_RADIO_BUTTONS);
});

const deliveryChoiceState = createSelector(
  [
    getDeliveryOptionsSelector,
    getShowAmendQuantityNotificationSelector,
    getShowClickCollectNowAvailableMessageSelector,
    getSubmitOrderFailedSelector,
    getShowCollectionHelpSelector,
    getTitleText,
    getIsExpressPayment,
    shouldShowThresholdBar,
    getTabsDisabled,
    getDeliveryCollectionRadioButtonsFeature,
    getOrderFormItemsSelector,
  ],
  (
    deliveryOptions,
    showAmendQuantityNotification,
    showClickCollectNowAvailableMessage,
    submitOrderFailed,
    showCollectionHelp,
    titleText,
    isExpressPayment,
    thresholdBarEnabled,
    tabsDisabled,
    deliveryCollectionRadioButtonsFeature,
    items,
  ) => ({
    deliveryOptions,
    showAmendQuantityNotification,
    showClickCollectNowAvailableMessage,
    submitOrderFailed,
    titleText,
    isExpressPayment,
    thresholdBarEnabled,
    tabsDisabled,
    deliveryCollectionRadioButtonsFeature,
    collectionNotAvailableMessage: showCollectionHelp
      ? 'Sorry, we can only offer collection when all items in your basket are eligible'
      : 'Collection is not available for your order',
    shipFromStoreItemsExist: items?.some(item => item.availability?.shipFromStore),
  }),
);

export default deliveryChoiceState;
